import React, { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '@/analytics';
import { BannerRow } from '@/components/GlobalBanner';
import { useAuth } from '@/contexts/auth';
import { useClient } from '@/hooks';

export interface WaPauseBannerI {
  /** Mensajes enviados en el ciclo (WA_msgs_sent). */
  used: number;
  /** Mensajes abonados en el ciclo, con el bonus del pack (WA_msgs_paid). */
  paid: number;
}

export const WA_PAUSE_BANNER_QUERY_ROOT = 'wa-pause-banner';

const HEALTHY_STALE_MS = 5 * 60_000;

/**
 * Cyclone decide si el negocio tiene los recordatorios pausados: misma audiencia que el banner
 * «110% Wapp» de Intercom al que reemplaza, resuelta con el gate de envío real.
 */
export const useWaPauseBanner = (): WaPauseBannerI | null => {
  const { session } = useAuth();
  const { client } = useClient();
  const enabled = !!session?.vendor?.id && !session.is_staff && !!session.vendor.is_onboarding_finished;

  const { data } = useQuery({
    queryKey: [WA_PAUSE_BANNER_QUERY_ROOT, session?.vendor?.id] as const,
    queryFn: () =>
      client<{ banner: WaPauseBannerI | null }>('me/vendor/plans/whatsapp-pause-banner', 'GET', {
        isAuthRequired: true,
        suppressNetworkToast: true,
        suppressNotFoundToast: true
      }),
    enabled,
    // Pausado: siempre fresco, para que se vaya apenas suma mensajes (vuelve de pagar con foco).
    // Sin pausa (casi todos) el foco revalida sólo si la respuesta tiene más de 5 minutos.
    staleTime: (query) => (query.state.data?.banner ? 0 : HEALTHY_STALE_MS),
    gcTime: Infinity,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: true
  });

  return data?.banner ?? null;
};

/**
 * «Recordatorios de WhatsApp pausados». Va fijo arriba del banner por prioridad del shell: lo ve
 * siempre quien está pausado, aunque tenga otro aviso (cobro, renovación, medios de cobro…).
 * Sin cerrar, como el de Intercom: se va solo cuando cyclone deja de pausar.
 */
export const WaPauseBanner: React.FC<{ banner: WaPauseBannerI }> = ({ banner }) => {
  const navigate = useNavigate();
  const tracked = useRef(false);

  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    trackEvent('wa_pause_banner_shown', { screen: 'App shell', used: banner.used, paid: banner.paid });
  }, [banner.paid, banner.used]);

  return (
    <BannerRow
      variant="error"
      message={`Recordatorios de WhatsApp pausados. Usaste ${banner.used} de los ${banner.paid} abonados.`}
      action={{
        label: 'Reactivar recordatorios',
        onClick: () => {
          trackEvent('wa_pause_banner_clicked', {
            screen: 'App shell',
            used: banner.used,
            paid: banner.paid
          });
          navigate('/facturacion/whatsapp-editar');
        }
      }}
    />
  );
};
